import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@tanstack/react-query';
import { useNotifications } from '@toolpad/core';
import { useForm } from 'react-hook-form';
import { FormContainer } from 'react-hook-form-mui';
import { useNavigate } from 'react-router';
import { DeleteForever } from '@mui/icons-material';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
} from '@mui/material';
import { GridColDef, GridRowParams } from '@mui/x-data-grid';

import {
    CategoryInput,
    DataGrid,
    DataGridCard,
} from '@barboxx/admin-components';
import {
    CREATE_CATEGORY_ASSIGNMENT_SCHEMA,
    ID_WIDTH,
    NAME_MIN_WIDTH,
} from '@barboxx/admin-utils';
import Api from '@barboxx/api';
import { CategoryAssignment, CustomDrink } from '@barboxx/models';
import { useBool } from '@barboxx/react-utils';
import { CATEGORY_TYPES } from '@barboxx/types';
import { enumToFormOptions } from '@barboxx/utils';

const COLUMNS: GridColDef<CategoryAssignment>[] = [
    {
        field: 'id',
        headerName: 'Id',
        width: ID_WIDTH,
    },
    {
        field: 'categoryType',
        headerName: 'Category Type',
        minWidth: NAME_MIN_WIDTH,
        type: 'singleSelect',
        valueOptions: enumToFormOptions(CATEGORY_TYPES),
    },
    {
        field: 'name',
        headerName: 'Name',
        minWidth: NAME_MIN_WIDTH,
        flex: 1,
    },
    {
        field: 'description',
        headerName: 'Description',
        minWidth: NAME_MIN_WIDTH,
        flex: 2,
    },
    {
        field: 'isActive',
        headerName: 'Is Active',
        minWidth: NAME_MIN_WIDTH,
        type: 'boolean',
    },
];

interface Props {
    customDrink: CustomDrink.DetailsResponse;
    refetch: () => void;
}

export const CategoriesListCard = ({ customDrink, refetch }: Props) => {
    const { id, categories } = customDrink;
    const navigate = useNavigate();
    const notifications = useNotifications();
    const [open, dialogActions] = useBool();

    const formContext = useForm<CategoryAssignment.CreateRequest>({
        resolver: yupResolver(CREATE_CATEGORY_ASSIGNMENT_SCHEMA),
        defaultValues: {
            customDrinkId: id,
            type: 'CustomDrink',
        },
    });

    const mutateCreateCategoryAssignment = useMutation({
        mutationKey: ['categoryAssignment'],
        mutationFn: (data: CategoryAssignment.CreateRequest) =>
            Api.Categories.Assignment.postCategoryAssignment(data),
    });

    const onSubmit = async (values: CategoryAssignment.CreateRequest) => {
        return mutateCreateCategoryAssignment
            .mutateAsync(values)
            .then(({ data }) => {
                notifications.show(`Created  category assignment!`, {
                    severity: 'success',
                });
                handleCancelCreate();
                refetch();
            })
            .catch(e => {
                notifications.show('Error creating category assignment!', {
                    severity: 'error',
                });
            });
    };

    const mutateDeleteAssignment = useMutation({
        mutationKey: ['deleteCategoryAssignment'],
        mutationFn: (data: CategoryAssignment.DeleteRequest) =>
            Api.Categories.Assignment.deleteCategoryAssignment(data),
    });

    const handleDeleteAssignment = async (
        values: CategoryAssignment.DeleteRequest
    ) => {
        return mutateDeleteAssignment
            .mutateAsync(values)
            .then(({ data }) => {
                notifications.show(`Deleted category assignment!`, {
                    severity: 'success',
                });
                refetch();
            })
            .catch(e => {
                notifications.show('Error deleting category assignment!', {
                    severity: 'error',
                });
            });
    };

    const handleCancelCreate = () => {
        formContext.reset();
        dialogActions.off();
    };

    const handleRowClick = (params: GridRowParams<CategoryAssignment>) => {
        navigate(`/core/categories/${params.row.categoryId}`);
    };

    const columns = COLUMNS.concat([
        {
            field: 'actions',
            headerName: 'Actions',
            minWidth: NAME_MIN_WIDTH,
            type: 'actions',
            getActions: params => [
                // <Button
                //     key="edit"
                //     onClick={() => {
                //         navigate(`${params.id}`);
                //     }}
                // >
                //     Edit
                // </Button>,
                <IconButton
                    key="delete"
                    onClick={() => {
                        handleDeleteAssignment(params.row);
                        // Handle delete action
                    }}
                >
                    <DeleteForever />
                </IconButton>,
            ],
        },
    ]);

    return (
        <>
            <DataGridCard
                title="Categories List"
                HeaderComponent={() => (
                    <Button variant="contained" onClick={dialogActions.on}>
                        Add Category
                    </Button>
                )}
            >
                <DataGrid
                    columns={columns}
                    disableColumnMenu
                    disableRowSelectionOnClick
                    paginationMode="client"
                    onRowClick={handleRowClick}
                    rows={categories}
                />
            </DataGridCard>
            <Dialog
                open={open}
                onClose={dialogActions.off}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
                fullWidth
            >
                <DialogTitle id="alert-dialog-title">
                    Create Product Variant
                </DialogTitle>
                <FormContainer onSuccess={onSubmit} formContext={formContext}>
                    <DialogContent
                        sx={{
                            gap: 2,
                            display: 'flex',
                            flexDirection: 'column',
                        }}
                    >
                        <CategoryInput />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCancelCreate} color="inherit">
                            Cancel
                        </Button>

                        <Button
                            color="primary"
                            variant="contained"
                            loading={formContext.formState.isSubmitting}
                            type="submit"
                            disabled={formContext.formState.isSubmitting}
                        >
                            Add
                        </Button>
                    </DialogActions>
                </FormContainer>
            </Dialog>
        </>
    );
};
